import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { green, grey, red, orange } from '@mui/material/colors';
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { cloneDeep, isEmpty, isEqual, isNil, isNull, isUndefined } from "lodash";

import resourceUtilizationService from "@plannel/services/mp/resource-utilization-service";
import planVersionService from "@plannel/services/data/plan-version-service";

import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import SplitterContainer from "@plannel/components/SplitterContainer";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import AutocompleteSearch from "@plannel/components/AutocompleteSearch";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"

import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import { FS } from "@plannel/utils/font-util";

const { mpMeasure, versionStatusCode, module } = CodeUtil;
const { RELEASED, PLANNED } = versionStatusCode;
const { MP } = module;
const { CAPA_TOTAL, CAPA_OVER_TOTAL, CAPA_LOAD, CAPA_SETUP, CAPA_OVER, CAPA_AVAIL, CAPA_UTIL, LIMIT_UTIL_RATE } = mpMeasure;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const { snackBarMessageColor } = ColorUtil;

const TIME_UOM_KEY_VALUE ={
  seconds: "S",
  minutes: "M",
  hour: "H"
};

const RESOURCE_TIME_UOM_ARRAY = ["seconds", "minutes", "hour"];

const FrozenCellRenderer = ({ value, valueFormatted, label }) => (
  <Tooltip title={label} placement="bottom-start">
    <Box component="span" sx={{ display: "block", width: "100%" }}>{valueFormatted ?? value}</Box>
  </Tooltip>
);

const LOCATION_NM_IGNORE_COL_ID = `LOC_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_NM_IGNORE_COL_ID = `RES_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ResourceUtilization = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const EXCEL_EXPORT = "excelExport";

  const pageSizes = [30, 50, 100];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(reduxViewState?.version ?? null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const [resource, setResource] = useState(reduxViewState?.resource ?? null);

  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [timeUom, setTimeUom] = useState(reduxViewState?.timeUom ?? RESOURCE_TIME_UOM_ARRAY[1]); // default minutes
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const gridHeaderMap = useRef({});
  const chartSelectedRowsRef = useRef([]); // 마지막 차트 선택 행 — 언어 변경 시 재조립용

  const gridRef = useRef();
  const columnDefs = useRef();
  const bucketColumnDefs = useRef([]);
  const exportBucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);
  const colsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "colsHide") ? reduxViewState.colsHide : true;

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(() => [CAPA_TOTAL, CAPA_OVER_TOTAL, CAPA_LOAD, CAPA_SETUP, CAPA_OVER, CAPA_AVAIL, CAPA_UTIL, LIMIT_UTIL_RATE], []);
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);

  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);

  const defaultOptions = { barSize: 20, strokeWidth: 2 };

  const chartSeries = useMemo(() => {
    return [
      { field: CAPA_LOAD, text: t(CAPA_LOAD), type: "bar",
        typeOptions: { stackId: "capa", fill: ColorUtil.colorSet(0), defaultOptions },
        groupName: t(timeUom)
      },
      { field: CAPA_SETUP, text: t(CAPA_SETUP), type: "bar",
        typeOptions: { stackId: "capa", fill: ColorUtil.colorSet(3), defaultOptions },
        groupName: t(timeUom)
      },
      { field: CAPA_AVAIL, text: t(CAPA_AVAIL), type: "bar",
        typeOptions: { stackId: "capa", fill: ColorUtil.colorSet(1), defaultOptions },
        groupName: t(timeUom)
      },
      { field: CAPA_OVER, text: t(CAPA_OVER), type: "bar",
        typeOptions: { stackId: "capa", fill: red[300], defaultOptions },
        groupName: t(timeUom)
      },
      { field: CAPA_UTIL, text: t(CAPA_UTIL), type: "line",
        typeOptions: { defaultOptions, stroke: ColorUtil.colorSet(2), fill: ColorUtil.colorSet(2) },
        orientation: "right",
        groupName: t(CAPA_UTIL)
      },
      { field: LIMIT_UTIL_RATE, text: t(LIMIT_UTIL_RATE), type: "line",
        typeOptions: { defaultOptions, stroke: orange[300], fill: orange[300] },
        orientation: "right",
        groupName: t(CAPA_UTIL)
      }
    ];

    // 언어가 바뀌면 t() 결과가 달라지므로 t 를 의존성에 둔다(빠지면 차트 텍스트가 옛 언어로 남는다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUom, t]);

  const pivotConfig = useMemo(() => {
    return {
      groupKey: "measure",
      chartSeries,
      include: chartLabel,
      xAxisSortMap: gridHeaderMap.current
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartLabel, chartSeries]);

  const getRowStyle = params => {
    if (params?.data?.measure && isEqual(params.data.measure, CAPA_TOTAL)) {
      return { background: green[50] };
    }

    return null;
  }

  const emptyValueFormatter = (params, isExport = false) => {
    const { data, colDef } = params;
    const fieldName = colDef?.field;
    const value = data?.[fieldName];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const percentColumns = [CAPA_UTIL, LIMIT_UTIL_RATE];

    if (percentColumns.includes(data.measure)) {
      const suffix = "%";
      if (isExport) {
        return NumberUtil.formatNumber(value / 100, { maximumFractionDigits: 5, minimumFractionDigits: 4 });
      } else {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: 3, minimumFractionDigits: 0 }) + suffix;
      }
    }

    let formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 3, minimumFractionDigits: 0 });

    if (isExport) {
      return formatValue.replace(/,/g, "");
    }

    return formatValue;
  }
  emptyValueFormatter.customType = EXCEL_EXPORT;

  const gridCellStyle = (params, headers) => {
    const { column, data, value } = params;

    const cellStyle = {};
    if (data && column) {
      const colFieldName = column.getColDef().field;
      const headerIndex = headers.indexOf(colFieldName);
      if (headerIndex > -1 && value !== null) {
        const isCapaUtilMeasure = isEqual(data.measure, CAPA_UTIL);
        const isLimitUtilRateMeasure = isEqual(data.measure, LIMIT_UTIL_RATE);

        if (isCapaUtilMeasure && value > 100) {
          cellStyle.background = red[100];
        }
        
        if (isLimitUtilRateMeasure && value > 100) {
          cellStyle.background = red[100];
        }
      }
    }

    if (isFrozenLoadCell(params)) {
      cellStyle.background = grey[300];
    }

    return cellStyle;
  }

  const isFrozenLoadCell = params => {
    const { data, colDef } = params;
    return isEqual(data?.measure, CAPA_LOAD) && !isNil(data?.frozenEndDate) && colDef?.field <= data.frozenEndDate;
  }

  const defaultColumnDefs = [
    {
      headerName: 'locCd', colId: "locCdGrp", width: 140, showRowGroup: 'locCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide,
        detailColumns: [LOCATION_NM_IGNORE_COL_ID]
      }, cellClass: 'stringType'
    },
    {
      headerName: 'locName', colId: LOCATION_NM_IGNORE_COL_ID, width: 160, showRowGroup: 'locName', pinned: "left", lockPinned: true, hide: colsHide,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'wcCd', colId: "wcCdGrp", cellClass: 'stringType', width: 140, showRowGroup: 'wcCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'resCd', colId: "resCdGrp", cellClass: 'stringType', width: 190, showRowGroup: 'resCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { suppressCount: true },
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide,
        detailColumns: [RESOURCE_NM_IGNORE_COL_ID]
      }
    },
    {
      headerName: 'resName', colId: RESOURCE_NM_IGNORE_COL_ID, width: 220, showRowGroup: 'resName', pinned: "left", lockPinned: true, hide: colsHide,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
    },
    { headerName: "locCd", field: "locCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locName", field: "locName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "wcCd", field: "wcCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "resCd", field: "resCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "resName", field: "resName", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "measure", field: "measure", width: 170, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
      valueFormatter: params => t(params.value), headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setMeasureModalOpen
      }
    }
  ];

  const defaultExportColumnDefs = [
    { field: "locCd", colId: "locCdGrp", rowGroup: true, hide: true },
    { field: "locName", colId: "locNmGrp", rowGroup: true, hide: true },
    { field: "wcCd", colId: "wcCdGrp", rowGroup: true, hide: true },
    { field: "resCd", colId: "resCdGrp", rowGroup: true, hide: true },
    { field: "resName", colId: "resNmGrp", rowGroup: true, hide: true },
    { headerName: "locCd", field: "locCd", width: 140, cellClass: 'stringType' },
    { headerName: "locName", field: "locName", width: 140 },
    { headerName: "wcCd", field: "wcCd", width: 140, cellClass: 'stringType' },
    { headerName: "resCd", field: "resCd", width: 140, cellClass: 'stringType' },
    { headerName: "resName", field: "resName", width: 140 },
    { headerName: "measure", field: "measure", width: 160, valueFormatter: (params) => t(params.value) }
  ];

  const sortMpVersions = versions => {
    const basicVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        basicVersions.push(ver);
      }
    }

    return [...basicVersions, ...releaseVersions];
  };

  const retrieveMpVersion = receivedVersion => {
    planVersionService.getAll({ moduleCd: MP, statusCds: [PLANNED, RELEASED], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          const sortedVersions = sortMpVersions(results);
          setVersions(sortedVersions);

          if (receivedVersion) {
            const currentVersion = results.find(result => isEqual(result.id, receivedVersion.id));
            currentVersion ? setVersion(currentVersion) : setVersion(results[0]);
          } else {
            if (reduxViewState?.version) {
              const reduxVersion = results.find(result => isEqual(result.id, reduxViewState.version.id));
              if (isUndefined(reduxVersion)) {
                setVersion(sortedVersions[0]);
              }
            } else {
              setVersion(sortedVersions[0]);
            }
          }
        } else {
          setVersions([]);
          setVersion(null);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (version) {
      params.versionId = version.id;
    }
    
    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (resource) {
      params.resCd = resource.resCd;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }

    return { ...params, ...sortParams };
  }

  const setGridHeaders = (headers, headerMap) => {
    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = version?.id ? GridUtils.IGNORE_P13N_COLLID + version.id : GridUtils.IGNORE_P13N_COLLID;
    const colDefs = cloneDeep(defaultColumnDefs);
    bucketColumnDefs.current = []
    exportBucketColumnDefs.current = [];

    headers.forEach((header) => {
      bucketColumnDefs.current.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle: params => gridCellStyle(params, headers),
        valueFormatter: emptyValueFormatter,
        cellRendererSelector: params => isFrozenLoadCell(params)
          ? { component: FrozenCellRenderer, params: { label: GridUtils.gridValueL10N("frozenRangeSim") } }
          : undefined
      });

      exportBucketColumnDefs.current.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: 'export_' + header + bucketPostFix,
        valueFormatter: emptyValueFormatter,
        cellClassRules: {
          greyBackground: isFrozenLoadCell,
          alertBackground: params => [CAPA_UTIL, LIMIT_UTIL_RATE].includes(params.data?.measure) && params.data?.[header] > 100
        }
      });
    });

    columnDefs.current = [...colDefs, ...bucketColumnDefs.current];
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...colDefs, ...bucketColumnDefs.current] });
  }

  const convertTimeValue = (value) => {
    if (isNil(value)) return 0;
    switch (timeUom) {
      case 'minutes':
        return value / 60;
      case 'hour':
        return value / 3600;
      case 'seconds':
      default:
        return value;
    }
};

  const setGridDatas = (headers, results) => {
    const datas = [];
    const capaTotalPivotData = "capaTotalPivotData";
    const capaOverTotalPivotData = "capaOverTotalPivotData";
    const capaLoadPivotData = "capaLoadPivotData";
    const capaSetupPivotData = "capaSetupPivotData";
    const capaOverPivotData = "capaOverPivotData";
    const capaAvailPivotData = "capaAvailPivotData";
    const capaUtilPivotData = "capaUtilPivotData";
    const limitUtilRatePivotData = "limitUtilRatePivotData";
   
    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;

      for (const measureName of measure) {
        const data = { ...resultsData, measure: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case CAPA_TOTAL: {
              data[header] = convertTimeValue(resultsData[capaTotalPivotData][idx]);
              break;
            }

            case CAPA_OVER_TOTAL: {
              data[header] = convertTimeValue(resultsData[capaOverTotalPivotData][idx]);
              break;
            }

            case CAPA_LOAD: {
              data[header] = convertTimeValue(resultsData[capaLoadPivotData][idx]);
              break;
            }

            case CAPA_SETUP: {
              data[header] = convertTimeValue(resultsData[capaSetupPivotData][idx]);
              break;
            }

            case CAPA_OVER: {
              data[header] = convertTimeValue(resultsData[capaOverPivotData][idx]);
              break;
            }

            case CAPA_AVAIL: {
              data[header] = convertTimeValue(resultsData[capaAvailPivotData][idx]);
              break;
            }

            case CAPA_UTIL:{
              data[header] = resultsData[capaUtilPivotData][idx];
              break;
            }

            case LIMIT_UTIL_RATE:{
              data[header] = resultsData[limitUtilRatePivotData][idx];
              break;
            }

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retrieveResourceUtilization = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    resourceUtilizationService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;

        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          setHeaders(localDateHeader);
          setGridHeaders(localDateHeader, headerMap);

          const datas = setGridDatas(localDateHeader, data);
          setData(datas);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          columnDefs.current = defaultColumnDefs;
          GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
          setHeaders([]);
          setData([]);
          setChartData([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { version, filter: filterValue, resource, timeUom, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });

    retrieveMpVersion();
  }

  const setChart = (selectedRows) => {
    const selectedDatas = [];
    const dateFormat = [];

    chartSelectedRowsRef.current = selectedRows ?? [];

    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    for (const header of headers) {
      dateFormat.push(gridHeaderMap.current[header]);
    }

    const capaTotalRow = selectedRows.filter(row => row.measure === CAPA_TOTAL)[0];
    for (const row of selectedRows) {
      const chartDatas = {};
      let inputFlag = false;

      for (const header of headers) {
        const series = [CAPA_LOAD, CAPA_SETUP, CAPA_OVER, CAPA_AVAIL, CAPA_UTIL, LIMIT_UTIL_RATE];
        if (series.includes(row.measure)) {
          const headerName = gridHeaderMap.current[header];
          let chartData = row[header];

          if (row.measure === CAPA_LOAD) {
            chartData = capaTotalRow ? (row[header] > capaTotalRow[header] ? capaTotalRow[header] : row[header]) : row[header];
          }

          chartDatas[headerName] = chartData;
          chartDatas.measure = t(row.measure);

          inputFlag = true;
        } else {
          break;
        }
      }

      if (inputFlag) {
        selectedDatas.push(chartDatas);
      }
    }
    setChartLabel(dateFormat);
    setChartData(selectedDatas);
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는
   * 그 값을 시리즈의 text 와 맞춰 어떤 Bar/Line 을 그릴지 고른다(chartSeries.find(text === dataKey)).
   * 시리즈만 새 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다.
   */
  useEffect(() => {
    if (isEmpty(chartSelectedRowsRef.current)) {
      return;
    }
    setChart(chartSelectedRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({nodes: selected, newValue: true, source: CHECKBOX_SELECTED});
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  }

  const toggleButtonHandler = (_, changeValue) => {
    if (isNil(changeValue) || isEqual(timeUom, changeValue)) return;

    setTimeUom(changeValue);
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0) {
      setChartData([]);
    } else {
      const firstNode = params.api.getDisplayedRowAtIndex(0);
      if (firstNode) {
        const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
        params.api.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
      }
    }
  }

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveResourceUtilization();
    }
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iconColor = planVersion => {
    if (planVersion) {
      const { statusCd } = planVersion;
      if (isEqual(statusCd, RELEASED)) {
        return grey[900];
      } else if (isEqual(statusCd, PLANNED)) {
        return green[600];
      }

      return red[600];
    }

    return null;
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }

    return null;
  }

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: defaultColumnDefs });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, viewName]);

  useEffect(() => {
    if (gridLoading && version && !isEmpty(measures)) {
      retrieveResourceUtilization();
    }

    return () => {
      setData([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, measures, sortParams]);

  useEffect(() => {
    if (gridLoading && version && !isEmpty(measures)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, filterValue, resource]);

  useEffect(() => {
    if (gridLoading && !isEmpty(data)) {
      retrieveResourceUtilization();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUom]);

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={versions}
          value={version}
          onChange={(_, newValue) => setVersion(newValue)}
          label={t("version")}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={setToolTip}
          minWidth={250}
          sx={{ mr: 1.5 }}
          inputIconSx={{ ml: 1 }}
        />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <AutocompleteSearch label="resource" value={resource} setValue={setResource} optionLabelProp="name" optionEqualProp="resCd"
          target="resource" /> 
        <ToggleButtonGroup
          color="primary"
          size="small"
          value={timeUom}
          sx={{ mr: 1 }}
          exclusive
          onChange={toggleButtonHandler}
        >
          {
            RESOURCE_TIME_UOM_ARRAY.map(tUom => {
              return (
                <ToggleButton key={tUom} value={tUom}>
                  <Tooltip title={t(tUom)}>
                    <Box component={"span"}>{TIME_UOM_KEY_VALUE[tUom]}</Box>
                  </Tooltip>
                </ToggleButton>
              );
            })
          }
        </ToggleButtonGroup>
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={resourceUtilizationService} setExcelData={setGridDatas}  columnDefs={[...defaultExportColumnDefs, ...exportBucketColumnDefs.current]}
            totalRows={resultTotalCount.current * rowsPerCombination} params={{ ...getRequestParams(false), measureCount: rowsPerCombination }} />
        </Box>
      </FilterContainer>
      <SplitterContainer>
        <Box sx={{ height: "100%" }}>
          <Box id="resource-utilization-grid" className="ag-theme-balham grid-area non-background-selected-row" mb={1} sx={{ height: "calc(100% - 50px)" }}>
            <AgGridReact
              ref={gridRef}
              rowData={data}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                groupSelects: "descendants",
              }}
              groupDisplayType={'custom'}
              groupDefaultExpanded={-1}
              groupHideOpenParents
              statusBar={statusBar}
              getRowStyle={getRowStyle}
              onRowSelected={onRowSelected}
              onCellClicked={onCellGridClicked}
              onRowDataUpdated={initCheck}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            >
            </AgGridReact>
          </Box>
          <PaginationContainer
            currentPage={currentPage} totalPages={gridPages}
            pageSize={pageSize} pageSizes={pageSizes}
            setCurrentPage={setCurrentPage} setPageSize={setPageSize}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "100%", paddingTop: '20px' }}>
          <PivotChartComposed
            pivotConfig={pivotConfig}
            data={chartData}
            options={{ tooltipContentStyle: { fontSize: FS.SUB }, yAxisDomain: [0, dataMax => ( 
              dataMax < 100 ? 100 : (dataMax === 0 ? dataMax + 1 : dataMax)
            )] }}
            nullConvert
            activeMeasures={!isEmpty(measures) ? measures : allMeasure}
          />
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
      />
    </>
  );
};

export default withTranslation()(ResourceUtilization);